"use client";

import { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";

import type { HomeFeatureBubble } from "@/types/home-messages";

export default function FeaturesBubblesSection() {
  const t = useTranslations("FeaturesBubbles");
  const items = (t.raw("items") as HomeFeatureBubble[]) ?? [];

  const [hoveredBubble, setHoveredBubble] = useState<string | null>(null);
  const [clickedBubble, setClickedBubble] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const currentElementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (clickedBubble) {
      const timer = setTimeout(() => {
        setClickedBubble(null);
        setTooltipPosition(null);
        currentElementRef.current = null;
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [clickedBubble]);

  const handleBubbleHover = (bubbleId: string, element: HTMLDivElement) => {
    setHoveredBubble(bubbleId);
    currentElementRef.current = element;
    const rect = element.getBoundingClientRect();
    setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
  };

  const handleBubbleLeave = () => {
    setHoveredBubble(null);
    if (!clickedBubble) {
      setTooltipPosition(null);
      currentElementRef.current = null;
    }
  };

  const handleBubbleClick = (bubbleId: string, element: HTMLDivElement) => {
    setClickedBubble(bubbleId);
    currentElementRef.current = element;
    const rect = element.getBoundingClientRect();
    setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
  };

  const updateTooltipPosition = (element: HTMLDivElement | null) => {
    if (element) {
      const rect = element.getBoundingClientRect();
      setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
    }
  };

  useEffect(() => {
    const activeId = hoveredBubble || clickedBubble;
    if (!activeId) return;

    let animationFrameId: number;
    const tick = () => {
      if (currentElementRef.current) updateTooltipPosition(currentElementRef.current);
      animationFrameId = requestAnimationFrame(tick);
    };
    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [hoveredBubble, clickedBubble]);

  const isActiveHighlight = (bubbleId: string) =>
    hoveredBubble === bubbleId || clickedBubble === bubbleId;

  const halfLength = Math.ceil(items.length / 2);
  const row1 = items.slice(0, halfLength);
  const row2 = items.slice(halfLength);
  const row1Bubbles = [...row1, ...row1, ...row1];
  const row2Bubbles = [...row2, ...row2, ...row2];

  const tooltipMap = Object.fromEntries(items.map((b) => [b.id, b.tooltip]));

  const renderBubble = (bubble: HomeFeatureBubble, index: number) => {
    const uniqueKey = `${bubble.id}-${index}`;
    return (
      <div
        key={uniqueKey}
        className="relative flex-shrink-0 p-1"
        onMouseEnter={(e) => {
          const target = e.currentTarget.querySelector(".bubble-inner") as HTMLDivElement;
          if (target) handleBubbleHover(bubble.id, target);
        }}
        onMouseLeave={handleBubbleLeave}
      >
        <motion.div
          className="bubble-inner flex cursor-pointer items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium shadow-md backdrop-blur-sm sm:space-x-1.5 sm:px-3 sm:py-1.5 md:px-4 md:py-2 md:text-sm"
          style={{ willChange: "transform, background-color, border, box-shadow" }}
          initial={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "#ffffff",
            border: "2px solid rgba(255, 255, 255, 0.2)",
          }}
          animate={{
            backgroundColor: isActiveHighlight(bubble.id) ? "#ffffff" : "rgba(255, 255, 255, 0.1)",
            color: isActiveHighlight(bubble.id) ? "#000000" : "#ffffff",
            border: isActiveHighlight(bubble.id)
              ? "2px solid #ff7800"
              : "2px solid rgba(255, 255, 255, 0.2)",
            scale: isActiveHighlight(bubble.id) ? 1.05 : 1,
            boxShadow: isActiveHighlight(bubble.id)
              ? "0 8px 20px rgba(255, 120, 0, 0.3)"
              : "0 0 0 rgba(0, 0, 0, 0)",
          }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          onClick={(e) => handleBubbleClick(bubble.id, e.currentTarget)}
        >
          <span className="text-xs sm:text-sm md:text-base">{bubble.icon}</span>
          <span className="whitespace-nowrap text-[10px] sm:text-xs md:text-sm">{bubble.label}</span>
        </motion.div>
      </div>
    );
  };

  const activeTooltipId = hoveredBubble || clickedBubble;

  if (items.length === 0) return null;

  return (
    <section
      className="relative z-50 w-full max-w-full overflow-hidden bg-gradient-to-br from-primary via-primary to-[#1a4f7a] py-3 sm:py-4"
      style={{ contain: "paint" }}
    >
      <div className="grid grid-cols-2 gap-2 px-3 sm:hidden">
        {items.map((bubble) => (
          <div
            key={`mobile-${bubble.id}`}
            className="relative min-w-0"
            onMouseEnter={(e) => handleBubbleHover(bubble.id, e.currentTarget)}
            onMouseLeave={handleBubbleLeave}
            onClick={(e) => handleBubbleClick(bubble.id, e.currentTarget)}
          >
            <motion.div
              className="bubble-inner flex min-h-9 cursor-pointer items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium shadow-md backdrop-blur-sm"
              style={{ willChange: "transform, background-color, border, box-shadow" }}
              initial={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "#ffffff",
                border: "2px solid rgba(255, 255, 255, 0.2)",
              }}
              animate={{
                backgroundColor: isActiveHighlight(bubble.id) ? "#ffffff" : "rgba(255, 255, 255, 0.1)",
                color: isActiveHighlight(bubble.id) ? "#000000" : "#ffffff",
                border: isActiveHighlight(bubble.id)
                  ? "2px solid #ff7800"
                  : "2px solid rgba(255, 255, 255, 0.2)",
                scale: isActiveHighlight(bubble.id) ? 1.02 : 1,
                boxShadow: isActiveHighlight(bubble.id)
                  ? "0 8px 20px rgba(255, 120, 0, 0.3)"
                  : "0 0 0 rgba(0, 0, 0, 0)",
              }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              <span className="shrink-0 text-xs">{bubble.icon}</span>
              <span className="min-w-0 text-[10px] leading-tight">{bubble.label}</span>
            </motion.div>
          </div>
        ))}
      </div>

      <div className="hidden w-full max-w-full space-y-2 overflow-hidden sm:block sm:space-y-3">
        <div className="relative h-9 w-full overflow-hidden sm:h-10 md:h-12">
          <div
            className="absolute left-0 top-0 flex gap-2 sm:gap-2.5 md:gap-3"
            style={{ animation: "home-bubbles-scroll 60s linear infinite" }}
          >
            {row1Bubbles.map((bubble, index) => renderBubble(bubble, index))}
          </div>
        </div>
        <div className="relative h-9 w-full overflow-hidden sm:h-10 md:h-12">
          <div
            className="absolute left-0 top-0 flex gap-2 sm:gap-2.5 md:gap-3"
            style={{ animation: "home-bubbles-scroll 60s linear infinite", animationDelay: "-30s" }}
          >
            {row2Bubbles.map((bubble, index) => renderBubble(bubble, index))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes home-bubbles-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>

      {activeTooltipId &&
        tooltipPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <motion.div
            key={activeTooltipId}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              left: `${tooltipPosition.x - 80}px`,
              top: `${tooltipPosition.y - 40}px`,
              transform: "translate(-50%, calc(-100% - 16px))",
              zIndex: 99999,
              pointerEvents: "none",
            }}
            className="rounded-lg bg-white px-2 py-1 text-[10px] text-primary shadow-lg whitespace-nowrap sm:px-3 sm:py-1.5 sm:text-xs"
          >
            {tooltipMap[activeTooltipId]}
            <div
              className="absolute left-1/2 h-0 w-0 -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-white"
              style={{ bottom: "-8px" }}
            />
          </motion.div>,
          document.body,
        )}
    </section>
  );
}
